/*!
 * Filename : Login.tsx
 * Author: Achmad Yusri Afandi (yusrideb@cpan.org)
 * Date Create : 8/19/26 9:16 PM
 */

import React, {FormEvent, useMemo, useState} from "react";

import styles from "../styles/login.module.scss";

type LoginProps = {
  dataPage?: {
    app_name?: string;
    app_desc?: string;
    name?: string;
    img?: {
      full?: string;
      login_logo?: string;
    };
  };
};

type LoginMessage = {
  type: "danger" | "info" | "success" | "warning";
  title: string;
  msg: string;
  forToken?: boolean;
};

export default function Login({dataPage = {}}: LoginProps) {
  const [isSubmitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<LoginMessage | null>(null);
  const appName = dataPage.app_name ?? "FinFlow";
  const appDesc = dataPage.app_desc ?? "Financial Flow";
  const logoPath = dataPage.img?.login_logo || dataPage.img?.full || "logo/landscape-logo.webp";
  const logoSrc = logoPath.startsWith("/") ? logoPath : "/content/" + logoPath;

  const formRules = useMemo(() => ({
    username: {min: 4, required: "Masukkan username", minMsg: "Username minimal 4 karakter"},
    password: {min: 4, required: "Masukkan password", minMsg: "Password minimal 4 karakter"},
  }), []);

  const onLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage(null);

    const form = event.currentTarget;
    const formData = new FormData(form);
    const username = String(formData.get("username") ?? "").trim();
    const password = String(formData.get("password") ?? "");
    const validationMessage = validateLogin(username, password, formRules);

    if (validationMessage) {
      setMessage(validationMessage);
      return;
    }

    const tokenUrl = parseAuthToken(dataPage.name);
    if (!tokenUrl) {
      setMessage({
        type: "info",
        title: "Error",
        msg: "Access Token have been expired, please reload this page",
        forToken: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`/youwant/${encodeURIComponent(tokenUrl)}`, {
        method: "POST",
        headers: {"X-Requested-With": "XMLHttpRequest"},
        body: formData,
      });
      const result = await response.json();

      if (result.code === 200) {
        setMessage({type: "success", title: "Info", msg: result.msg});
        window.setTimeout(() => window.location.reload(), 800);
        return;
      }

      setMessage({
        type: result.code === 406 ? "danger" : result.code === 500 ? "info" : "warning",
        title: "Error",
        msg: result.msg ?? "Login request failed",
        forToken: result.code === 500,
      });
    } catch {
      setMessage({type: "danger", title: "Error", msg: "Tidak dapat menghubungi server login"});
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className={styles.panel}>
      <div className={styles.brand}>
        <img className={styles.logo} src={logoSrc} alt={appName} />
        <div>
          <h1>{appName}</h1>
          <p>{appDesc}</p>
        </div>
      </div>

      <form
        className={styles.form}
        onSubmit={onLogin}
      >
        {message ? <LoginAlert data={message} /> : null}

        <label className={styles.field}>
          <span>Username</span>
          <input name="username" type="text" autoComplete="username" placeholder="Masukkan username ..." />
        </label>

        <label className={styles.field}>
          <span>Password</span>
          <div className={styles.passwordGroup}>
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Masukkan password ..."
            />
            <button
              className={styles.passwordToggle}
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </label>

        <button className={styles.submit} type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Login..." : "Login"}
        </button>
      </form>
    </main>
  );
}

function validateLogin(username: string, password: string, rules: ReturnType<typeof createRules>): LoginMessage | null {
  const errors: string[] = [];
  if (!username) errors.push(rules.username.required);
  if (username && username.length < rules.username.min) errors.push(rules.username.minMsg);
  if (!password) errors.push(rules.password.required);
  if (password && password.length < rules.password.min) errors.push(rules.password.minMsg);

  if (!errors.length) return null;
  return {type: "danger", title: "Error", msg: errors.join(". ")};
}

function createRules() {
  return {
    username: {min: 4, required: "Masukkan username", minMsg: "Username minimal 4 karakter"},
    password: {min: 4, required: "Masukkan password", minMsg: "Password minimal 4 karakter"},
  };
}

function parseAuthToken(cookieName?: string) {
  if (!cookieName || typeof document === "undefined") return "";

  const cookie = document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(`${cookieName}=`));

  if (!cookie) return "";
  const value = cookie.slice(cookieName.length + 1);
  return base64Encode(value);
}

function base64Encode(value: string) {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
}

function LoginAlert({data}: {data: LoginMessage}) {
  return (
    <div className={[styles.message, styles[data.type]].join(" ")} role="alert">
      <strong>{data.title}: </strong>
      <span>{data.msg}</span>
      {data.forToken ? (
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      ) : null}
    </div>
  );
}
